
import React, { useState, useCallback } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import { CheckCircle2, Loader2, Mail, UserPlus, Briefcase, Heart, Eye, Shield, Plus, X } from 'lucide-react'
import { API_BASE_URL } from '@/shared/services/api/api.config'

interface FollowUpStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    onComplete?: () => Promise<void> | void
    [key: string]: any
}

interface ReferralEmail {
    id: string
    email: string
}

export default function FollowUpStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    onComplete
}: FollowUpStepProps) {
    const [isProcessing, setIsProcessing] = useState(false)
    const [isCompleting, setIsCompleting] = useState(false)
    const [processedWorkflows, setProcessedWorkflows] = useState<string[]>([])
    const [errors, setErrors] = useState<Record<string, string>>({})

    // Health Insurance Referral Emails
    const [healthReferralEmails, setHealthReferralEmails] = useState<ReferralEmail[]>(
        enrollmentData.healthReferralEmails || []
    )
    const [newHealthEmail, setNewHealthEmail] = useState('')

    // Recruiting Referral Email
    const [recruitingReferralEmail, setRecruitingReferralEmail] = useState(
        enrollmentData.recruitingReferralEmail || ''
    )

    // Allow agent to override/fix client email right in the follow-up step
    const [clientEmail, setClientEmail] = useState(enrollmentData.clientEmail || '')
    const clientName = `${enrollmentData.clientFirstName || ''} ${enrollmentData.clientLastName || ''}`.trim() || undefined
    const agentName = `${enrollmentData.referralAgentFirstName || ''} ${enrollmentData.referralAgentLastName || ''}`.trim() || undefined

    const handleAddHealthReferral = () => {
        if (!newHealthEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newHealthEmail)) {
            setErrors({ ...errors, healthReferral: 'Please enter a valid email' })
            return
        }
        const newReferral = { id: Date.now().toString(), email: newHealthEmail }
        const updated = [...healthReferralEmails, newReferral]
        setHealthReferralEmails(updated)
        updateEnrollmentData('healthReferralEmails', updated)
        setNewHealthEmail('')
        setErrors({ ...errors, healthReferral: '' })
    }

    const handleRemoveHealthReferral = (id: string) => {
        const updated = healthReferralEmails.filter(r => r.id !== id)
        setHealthReferralEmails(updated)
        updateEnrollmentData('healthReferralEmails', updated)
    }

    const handleApplyChanges = useCallback(async () => {
        setErrors({})

        // Determine whether any workflow needs the client's email
        const needsClientEmail = [
            enrollmentData.wantsDentalInsurance === 'Yes',
            enrollmentData.wantsVisionInsurance === 'Yes',
            enrollmentData.wantsLifeInsurance === 'Yes',
            enrollmentData.wantsAncillaryInsurance === 'Yes',
            enrollmentData.referDentalInsurance === 'Yes',
            enrollmentData.referVisionInsurance === 'Yes',
            enrollmentData.referLifeInsurance === 'Yes',
            enrollmentData.referAncillaryInsurance === 'Yes',
            enrollmentData.clientWantsJob === 'Yes',
            enrollmentData.clientWantsToReferForJob === 'Yes',
        ].some(Boolean)

        if (needsClientEmail && !clientEmail) {
            setErrors({ general: "Client email is missing. Please go back and ensure the client's email is filled in before sending links." })
            return
        }

        setIsProcessing(true)
        setProcessedWorkflows([])

        const workflows: string[] = []

        try {
            // Additional Insurance Workflows
            if (enrollmentData.wantsDentalInsurance === 'Yes') {
                await sendInsuranceLink(clientEmail, 'dental', clientName)
                workflows.push('Dental Insurance link sent')
            }
            if (enrollmentData.wantsVisionInsurance === 'Yes') {
                await sendInsuranceLink(clientEmail, 'vision', clientName)
                workflows.push('Vision Insurance link sent')
            }
            if (enrollmentData.wantsLifeInsurance === 'Yes') {
                await sendInsuranceLink(clientEmail, 'life', clientName)
                workflows.push('Life Insurance link sent')
            }
            if (enrollmentData.wantsAncillaryInsurance === 'Yes') {
                await sendInsuranceLink(clientEmail, 'ancillary', clientName)
                workflows.push('Ancillary Insurance link sent')
            }

            // Referral Workflows
            if (enrollmentData.referHealthInsurance === 'Yes' && healthReferralEmails.length > 0) {
                for (const referral of healthReferralEmails) {
                    await sendHealthReferral(referral.email, agentName)
                    workflows.push(`Health Insurance referral sent to ${referral.email}`)
                }
            }
            if (enrollmentData.referDentalInsurance === 'Yes') {
                await sendReferralLink(clientEmail, 'dental', clientName)
                workflows.push('Dental referral link sent')
            }
            if (enrollmentData.referVisionInsurance === 'Yes') {
                await sendReferralLink(clientEmail, 'vision', clientName)
                workflows.push('Vision referral link sent')
            }
            if (enrollmentData.referLifeInsurance === 'Yes') {
                await sendReferralLink(clientEmail, 'life', clientName)
                workflows.push('Life referral link sent')
            }
            if (enrollmentData.referAncillaryInsurance === 'Yes') {
                await sendReferralLink(clientEmail, 'ancillary', clientName)
                workflows.push('Ancillary referral link sent')
            }
            if (enrollmentData.referMedicareInsurance === 'Yes') {
                workflows.push('Medicare referral - Milestone 3 workflow queued')
            }

            // Recruiting Workflows
            if (enrollmentData.clientWantsJob === 'Yes') {
                await sendOnboardingLink(clientEmail, clientName)
                workflows.push('Job onboarding link sent to client')
            }
            if (enrollmentData.clientWantsToReferForJob === 'Yes' && recruitingReferralEmail) {
                await sendOnboardingLink(clientEmail, clientName)
                await sendRecruitReferral(recruitingReferralEmail, clientEmail, undefined, agentName)
                workflows.push(`Recruiting referral sent to ${recruitingReferralEmail}`)
            }

            // If no workflows were selected (all "No"), add a completion message
            if (workflows.length === 0) {
                workflows.push('No additional workflows selected - enrollment completed')
            }

            setProcessedWorkflows(workflows)

            // Mark enrollment as fully complete (allow all "No" selections)
            updateEnrollmentData('followUpComplete', true)
            updateEnrollmentData('followUpWorkflows', workflows)

        } catch (error: any) {
            console.error('Workflow processing error:', error)
            const message = error?.message || 'An unexpected error occurred.'
            setErrors({ general: `Workflow failed: ${message}` })
        } finally {
            setIsProcessing(false)
        }
    }, [enrollmentData, clientEmail, healthReferralEmails, recruitingReferralEmail, updateEnrollmentData])

    const YesNoQuestion = ({
        label,
        field,
        icon: Icon
    }: {
        label: string;
        field: string;
        icon: React.ElementType
    }) => (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">{label}</span>
            </div>
            <div className="flex gap-4">
                <label className="flex items-center">
                    <input
                        type="radio"
                        name={field}
                        value="Yes"
                        checked={enrollmentData[field] === 'Yes'}
                        onChange={() => updateEnrollmentData(field, 'Yes')}
                        className="h-4 w-4 text-indigo-600"
                    />
                    <span className="ml-2 text-sm">Yes</span>
                </label>
                <label className="flex items-center">
                    <input
                        type="radio"
                        name={field}
                        value="No"
                        checked={enrollmentData[field] === 'No'}
                        onChange={() => updateEnrollmentData(field, 'No')}
                        className="h-4 w-4 text-gray-600"
                    />
                    <span className="ml-2 text-sm">No</span>
                </label>
            </div>
        </div>
    )

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 5}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            isNavigating={isNavigating}
            onComplete={onComplete}
            showNextButton={false}
            showBackButton={false}
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 6: Follow-Up</h3>
                        <p className="text-sm text-accent-600">Additional insurance, referrals, and recruiting opportunities.</p>
                    </div>
                </div>

                {/* Success Banner */}
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">Enrollment Successfully Submitted!</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">Complete the follow-up options below to maximize client value.</p>
                </div>

                <div className="space-y-8">
                    {/* SECTION: Additional Insurance */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            Additional Insurance
                        </h4>
                        <div className="space-y-2 mb-4">
                            <label className="text-sm font-medium text-gray-700">
                                Client Email {!clientEmail && <span className="text-red-500 ml-1">(Required — please enter below)</span>}
                            </label>
                            <input
                                type="email"
                                value={clientEmail}
                                onChange={(e) => {
                                    setClientEmail(e.target.value)
                                    updateEnrollmentData('clientEmail', e.target.value)
                                }}
                                placeholder="client@example.com"
                                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${!clientEmail ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                            />
                            {clientEmail && <p className="text-xs text-gray-500">Links will be sent to this address.</p>}
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Select YES to send self-enrollment links to the client's email ({clientEmail || 'N/A'})
                        </p>
                        <div className="space-y-3">
                            <YesNoQuestion label="Does Client Want Dental Insurance?" field="wantsDentalInsurance" icon={Heart} />
                            <YesNoQuestion label="Does Client Want Vision Insurance?" field="wantsVisionInsurance" icon={Eye} />
                            <YesNoQuestion label="Does Client Want Life Insurance?" field="wantsLifeInsurance" icon={Shield} />
                            <YesNoQuestion label="Does Client Want Ancillary Insurance?" field="wantsAncillaryInsurance" icon={Plus} />
                        </div>
                    </div>

                    {/* SECTION: Referrals */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center gap-2">
                            <UserPlus className="w-5 h-5" />
                            Referrals
                        </h4>
                        <div className="space-y-4">
                            {/* Health Insurance Referral with Email Input */}
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <Heart className="w-5 h-5 text-blue-600" />
                                        <span className="font-medium text-gray-700">Does Client Want To Refer for Health Insurance?</span>
                                    </div>
                                    <div className="flex gap-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="referHealthInsurance"
                                                value="Yes"
                                                checked={enrollmentData.referHealthInsurance === 'Yes'}
                                                onChange={() => updateEnrollmentData('referHealthInsurance', 'Yes')}
                                                className="h-4 w-4 text-indigo-600"
                                            />
                                            <span className="ml-2 text-sm">Yes</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="referHealthInsurance"
                                                value="No"
                                                checked={enrollmentData.referHealthInsurance === 'No'}
                                                onChange={() => updateEnrollmentData('referHealthInsurance', 'No')}
                                                className="h-4 w-4 text-gray-600"
                                            />
                                            <span className="ml-2 text-sm">No</span>
                                        </label>
                                    </div>
                                </div>

                                {enrollmentData.referHealthInsurance === 'Yes' && (
                                    <div className="mt-3 space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Referral Email Addresses:</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="email"
                                                value={newHealthEmail}
                                                onChange={(e) => setNewHealthEmail(e.target.value)}
                                                placeholder="referral@email.com"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                            />
                                            <button
                                                onClick={handleAddHealthReferral}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        {errors.healthReferral && <p className="text-sm text-red-600">{errors.healthReferral}</p>}
                                        {healthReferralEmails.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {healthReferralEmails.map(r => (
                                                    <span key={r.id} className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-full text-sm border">
                                                        <Mail className="w-3 h-3" />
                                                        {r.email}
                                                        <button onClick={() => handleRemoveHealthReferral(r.id)} className="ml-1 text-red-500 hover:text-red-700">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <p className="text-xs text-blue-600">Each referral will receive a self-enrollment intake link.</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3">
                                <YesNoQuestion label="Does Client Want To Refer for Dental Insurance?" field="referDentalInsurance" icon={Heart} />
                                <YesNoQuestion label="Does Client Want To Refer for Vision Insurance?" field="referVisionInsurance" icon={Eye} />
                                <YesNoQuestion label="Does Client Want To Refer for Life Insurance?" field="referLifeInsurance" icon={Shield} />
                                <YesNoQuestion label="Does Client Want To Refer for Ancillary Insurance?" field="referAncillaryInsurance" icon={Plus} />
                                <div className="p-4 bg-gray-100 rounded-lg border border-gray-300">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Shield className="w-5 h-5 text-gray-400" />
                                            <span className="font-medium text-gray-500">Does Client Want To Refer for Medicare Insurance?</span>
                                            <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">Milestone 3</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="referMedicareInsurance"
                                                    value="Yes"
                                                    checked={enrollmentData.referMedicareInsurance === 'Yes'}
                                                    onChange={() => updateEnrollmentData('referMedicareInsurance', 'Yes')}
                                                    className="h-4 w-4 text-indigo-600"
                                                />
                                                <span className="ml-2 text-sm">Yes</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="referMedicareInsurance"
                                                    value="No"
                                                    checked={enrollmentData.referMedicareInsurance === 'No'}
                                                    onChange={() => updateEnrollmentData('referMedicareInsurance', 'No')}
                                                    className="h-4 w-4 text-gray-600"
                                                />
                                                <span className="ml-2 text-sm">No</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECTION: Recruiting */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center gap-2">
                            <Briefcase className="w-5 h-5" />
                            Recruiting
                        </h4>
                        <div className="space-y-4">
                            <YesNoQuestion label="Does Client Want a Job?" field="clientWantsJob" icon={Briefcase} />

                            {/* Job Referral with Email Input */}
                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <UserPlus className="w-5 h-5 text-purple-600" />
                                        <span className="font-medium text-gray-700">Does Client Want to Refer Someone for a Job?</span>
                                    </div>
                                    <div className="flex gap-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="clientWantsToReferForJob"
                                                value="Yes"
                                                checked={enrollmentData.clientWantsToReferForJob === 'Yes'}
                                                onChange={() => updateEnrollmentData('clientWantsToReferForJob', 'Yes')}
                                                className="h-4 w-4 text-indigo-600"
                                            />
                                            <span className="ml-2 text-sm">Yes</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="clientWantsToReferForJob"
                                                value="No"
                                                checked={enrollmentData.clientWantsToReferForJob === 'No'}
                                                onChange={() => updateEnrollmentData('clientWantsToReferForJob', 'No')}
                                                className="h-4 w-4 text-gray-600"
                                            />
                                            <span className="ml-2 text-sm">No</span>
                                        </label>
                                    </div>
                                </div>

                                {enrollmentData.clientWantsToReferForJob === 'Yes' && (
                                    <div className="mt-3 space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Referral Email Address:</label>
                                        <input
                                            type="email"
                                            value={recruitingReferralEmail}
                                            onChange={(e) => {
                                                setRecruitingReferralEmail(e.target.value)
                                                updateEnrollmentData('recruitingReferralEmail', e.target.value)
                                            }}
                                            placeholder="referral@email.com"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        />
                                        <p className="text-xs text-purple-600">
                                            Referral will be associated as downline for client. 30-day window to create account.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Processed Workflows */}
                    {processedWorkflows.length > 0 && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h5 className="font-semibold text-green-800 mb-2">✅ Workflows Processed:</h5>
                            <ul className="space-y-1 mb-4">
                                {processedWorkflows.map((workflow, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-sm text-green-700">
                                        <CheckCircle2 className="w-4 h-4" />
                                        {workflow}
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={async () => {
                                    setIsCompleting(true)
                                    try {
                                        if (onComplete) {
                                            await onComplete()
                                        }
                                    } catch (e) {
                                        console.error(e)
                                        setIsCompleting(false)
                                    }
                                }}
                                disabled={isNavigating || isCompleting}
                                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold text-base hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isNavigating || isCompleting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Saving Enrollment...</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-5 h-5" />
                                        Complete Enrollment
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {errors.general && (
                        <p className="text-sm text-red-600">{errors.general}</p>
                    )}

                    {/* Apply Changes Button */}
                    <div className="pt-4 border-t">
                        <button
                            onClick={handleApplyChanges}
                            disabled={isProcessing}
                            className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold text-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Processing Workflows...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-5 h-5" />
                                    {processedWorkflows.length > 0 ? 'Re-apply Changes' : 'Apply Changes'}
                                </>
                            )}
                        </button>
                        <p className="text-center text-sm text-gray-500 mt-2">
                            {processedWorkflows.length > 0
                                ? 'Click above to complete the enrollment, or update selections and re-apply.'
                                : 'This will trigger all YES workflows and send links to the client.'}
                        </p>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}

// API Helper Functions
const getAuthHeader = (): Record<string, string> => {
    const token = document.cookie.split('; ').find(r => r.startsWith('token='))?.split('=')[1]
        || localStorage.getItem('token')
        || ''
    return token ? { 'Authorization': `Bearer ${token}` } : {}
}

async function sendInsuranceLink(email: string, type: string, clientName?: string): Promise<void> {
    try {
        const res = await fetch(`${API_BASE_URL}/enrollments/send-insurance-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ email, insuranceType: type, clientName })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (error) {
        console.error(`Failed to send ${type} insurance link:`, error)
        throw error
    }
}

async function sendHealthReferral(email: string, agentName?: string): Promise<void> {
    try {
        const res = await fetch(`${API_BASE_URL}/enrollments/send-health-referral`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ referralEmail: email, agentName })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (error) {
        console.error('Failed to send health referral:', error)
        throw error
    }
}

async function sendReferralLink(clientEmail: string, type: string, clientName?: string): Promise<void> {
    try {
        const res = await fetch(`${API_BASE_URL}/enrollments/send-referral-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ clientEmail, referralType: type, clientName })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (error) {
        console.error(`Failed to send ${type} referral link:`, error)
        throw error
    }
}

async function sendOnboardingLink(email: string, clientName?: string): Promise<void> {
    try {
        const res = await fetch(`${API_BASE_URL}/enrollments/send-onboarding-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ email, clientName })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (error) {
        console.error('Failed to send onboarding link:', error)
        throw error
    }
}

async function sendRecruitReferral(referralEmail: string, referrerEmail: string, referralName?: string, referrerName?: string): Promise<void> {
    try {
        const res = await fetch(`${API_BASE_URL}/enrollments/send-recruit-referral`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ referralEmail, referrerEmail, referralName, referrerName })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (error) {
        console.error('Failed to send recruit referral:', error)
        throw error
    }
}
